import { Palette, Heart, Award, Users } from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-artsy-background">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="font-heading text-5xl md:text-6xl font-bold text-slate-900 mb-6">
                About <span className="text-teal-600">Artsy</span> <span className="text-pink-500">Craftsy</span>
              </h1>
              <p className="font-body text-lg text-slate-600 mb-6">
                Artsy Craftsy is an after-school art club designed to inspire children in Years 1-6 to explore their creativity, develop artistic skills, and build confidence in a supportive environment.
              </p>
              <p className="font-body text-lg text-slate-600">
                We believe every child is an artist, and our mission is to nurture that creativity through engaging, hands-on art experiences that encourage self-expression and artistic growth.
              </p>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-pink-400 rounded-3xl transform -rotate-3 border-2 border-slate-900"></div>
              <img 
                src="https://images.unsplash.com/photo-1703301287688-c9a306ebed99?w=600&h=450&fit=crop"
                alt="Children creating art together"
                className="relative rounded-3xl border-2 border-slate-900 shadow-sticker-xl w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl font-bold text-slate-900 text-center mb-12">
            Our <span className="text-teal-600">Values</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <Palette className="w-8 h-8" />,
                title: "Creativity First",
                description: "We encourage experimentation and creative thinking in every session.",
                color: "bg-teal-600"
              },
              {
                icon: <Heart className="w-8 h-8" />,
                title: "Supportive Environment",
                description: "A safe, nurturing space where every child can express themselves freely.",
                color: "bg-pink-500"
              },
              {
                icon: <Award className="w-8 h-8" />,
                title: "Skill Development",
                description: "Progressive learning that builds confidence and artistic techniques.",
                color: "bg-yellow-500"
              },
              {
                icon: <Users className="w-8 h-8" />,
                title: "Community",
                description: "Building connections through shared creative experiences.",
                color: "bg-purple-500"
              }
            ].map((value, index) => (
              <div key={index} className="card-sticker text-center">
                <div className={`w-16 h-16 ${value.color} rounded-2xl flex items-center justify-center text-white mx-auto mb-4 border-2 border-slate-900 shadow-sticker`}>
                  {value.icon}
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">{value.title}</h3>
                <p className="font-body text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-4xl font-bold text-slate-900 text-center mb-12">
            What We <span className="text-pink-500">Offer</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-sticker">
              <h3 className="font-heading text-2xl font-bold text-teal-600 mb-4">Art Techniques</h3>
              <ul className="space-y-3 font-body text-slate-600">
                <li>• Drawing and sketching fundamentals</li>
                <li>• Watercolour and acrylic painting</li>
                <li>• Oil pastel techniques</li>
                <li>• Mixed media projects</li>
                <li>• Colour theory and composition</li>
              </ul>
            </div>
            <div className="card-sticker">
              <h3 className="font-heading text-2xl font-bold text-pink-500 mb-4">Learning Environment</h3>
              <ul className="space-y-3 font-body text-slate-600">
                <li>• Small class sizes (max 20 children)</li>
                <li>• Professional art materials provided</li>
                <li>• Weekly progress updates for parents</li>
                <li>• End-of-term art exhibition</li>
                <li>• Supportive and encouraging atmosphere</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Teacher */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl font-bold text-slate-900 mb-8">
            Meet Your <span className="text-teal-600">Art Teacher</span>
          </h2>
          <div className="card-pricing inline-block text-left">
            <div className="flex flex-col md:flex-row gap-6 items-center">
              <div className="w-32 h-32 bg-gradient-to-br from-teal-400 to-pink-400 rounded-full border-2 border-slate-900 shadow-sticker flex-shrink-0"></div>
              <div>
                <h3 className="font-heading text-2xl font-bold text-slate-900 mb-2">Passionate Art Educator</h3>
                <p className="font-body text-slate-600">
                  With a deep love for children's art education, our teacher brings creativity and enthusiasm to every session. 
                  Each class is designed to be fun, educational, and inspiring, helping children discover their unique artistic voice.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
